import { formatWorkOrderNumber } from '@zspace/format';
import {
  ApplicationVariantType,
  PaginatedAPIResponse,
  SalesOrderData,
  SalesOrderDevicesTableRow,
  SalesOrderSoftwareTitlesTableRow,
  SortDirection,
  User,
  UserRoleType,
  UsersCriteria,
} from '@zspace/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import {
  FaBolt,
  FaBuilding,
  FaCalendar,
  FaGear,
  FaLaptop,
  FaUser,
} from 'react-icons/fa6';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import Conditional from '../../shared/conditional/conditional';
import useHttpRequest from '../../shared/hooks/http-request';
import useIsCustomerUser from '../../shared/hooks/is-customer-user';
import useIsZspaceInternalUser from '../../shared/hooks/is-zspace-internal-user';
import If from '../../shared/if/if';
import {
  extractEmailDomain,
  formatDateToLocaleString,
} from '../../shared/utils';
import DetailsCard from '../../ui/details-card/details-card';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import InboxTable from '../../ui/inbox-table/inbox-table';
import InfoLabel from '../../ui/info-label/info-label';
import Link from '../../ui/link/link';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import UsersSummaryTable from '../../users/users-summary-table/users-summary-table';
import { PermissionsGroupBySalesOrder } from '../../users/users-table/users-table';
import SalesOrderDevicesTable from '../sales-order-devices-table/sales-order-devices-table';
import SalesOrderSoftwareTitlesTable from '../sales-order-software-titles-table/sales-order-software-titles-table';
import { fetchSalesOrderRelatedUsers } from '../sales-orders-service';
import styles from './sales-order-details-layout.module.scss';

export type SalesOrderDetailsLayoutProps = {
  salesOrder: SalesOrderData;
  summaryLayout?: boolean;
  adminViewMode?: boolean;
};

const initialUsersCriteria = ({
  searchParams,
}: {
  searchParams?: URLSearchParams;
  salesOrder?: SalesOrderData;
} = {}): UsersCriteria => ({
  itemsPerPage: parseInt(searchParams?.get('itemsPerPage') ?? '10'),
  pageNumber: parseInt(searchParams?.get('pageNumber') ?? '1'),
  search: searchParams?.get('search') ?? '',
  sortBy: searchParams?.get('sortBy') ?? 'email',
  sortDirection:
    (searchParams?.get('sortDirection') as SortDirection) ?? SortDirection.ASC,
});

export function SalesOrderDetailsLayout({
  salesOrder,
  summaryLayout = false,
  adminViewMode = false,
}: SalesOrderDetailsLayoutProps) {
  const params = useParams();
  const isCustomerUser = useIsCustomerUser();
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [relatedUsers, setRelatedUsers] = useState<User[]>([]);
  const [criteria, setCriteria] = useState<UsersCriteria>(
    initialUsersCriteria({ searchParams, salesOrder })
  );
  const { executeHttpRequest: executeFetchMoreRelatedUsers } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchRelatedUsers,
    isLoading: isLoadingMyDeviceGroups,
  } = useHttpRequest();
  const [relatedUsersDataResponse, setRelatedUsersDataResponse] = useState<
    PaginatedAPIResponse<User>
  >({
    data: [],
    count: 0,
    page: 0,
    total: 0,
    pages: 0,
    hasMore: true,
  });

  const salesOrderUserType = useMemo(() => {
    const isInternalSalesOrder = environment.internalUserEmailDomains
      .split(',')
      .includes(extractEmailDomain(salesOrder.endUser?.email ?? '')!);
    return isInternalSalesOrder
      ? UserRoleType.ZSPACE_INTERNAL
      : UserRoleType.CUSTOMER;
  }, [salesOrder.endUser?.email]);

  const salesOrderRelatedUsers = useMemo(() => {
    const users: User[] = [];
    const invalidUsers: string[] = [];

    for (const user of relatedUsers) {
      const isValidSalesOrderRelatedUser = user.roles.some(
        (roleAssignment) =>
          roleAssignment.role.name === salesOrderUserType &&
          roleAssignment.permissionsGroupUserRoleAssignments.length > 0
      );

      if (isValidSalesOrderRelatedUser) {
        users.push(user);
      } else {
        invalidUsers.push(user.email);
      }
    }

    return { users, invalidUsers };
  }, [relatedUsers, salesOrderUserType]);

  const invalidUsersMessage = useMemo(() => {
    let message: string | null = null;
    const salesOrderUserTypeText =
      salesOrderUserType === UserRoleType.ZSPACE_INTERNAL
        ? 'internal'
        : 'customer';

    if (
      salesOrderRelatedUsers.invalidUsers.length > 0 &&
      isZspaceInternalUser
    ) {
      message = `The following email(s) are incompatible with ${salesOrderUserTypeText} Sales Orders: ${salesOrderRelatedUsers.invalidUsers.join(
        ', '
      )}`;
    }

    return message;
  }, [
    isZspaceInternalUser,
    salesOrderRelatedUsers.invalidUsers,
    salesOrderUserType,
  ]);

  const fetchRelatedUsers = useCallback(
    async (criteria: UsersCriteria) => {
      executeFetchRelatedUsers({
        asyncFunction: async () => {
          const salesOrderId = params.id! ?? salesOrder.id;
          const usersResponse = await fetchSalesOrderRelatedUsers(
            salesOrderId,
            criteria
          );
          setRelatedUsers(usersResponse.data);
          setRelatedUsersDataResponse(usersResponse);
        },
      });
    },
    [params.id, executeFetchRelatedUsers, salesOrder.id]
  );

  const fetchMoreRelatedUsers = useCallback(async () => {
    executeFetchMoreRelatedUsers({
      asyncFunction: async () => {
        const nextPage = criteria.pageNumber + 1;
        const updatedCriteria = {
          ...criteria,
          pageNumber: nextPage,
        };
        const salesOrderId = params.id! ?? salesOrder.id;
        const usersResponse = await fetchSalesOrderRelatedUsers(
          salesOrderId,
          updatedCriteria
        );

        setRelatedUsers((prevRelatedUsers) => [
          ...prevRelatedUsers,
          ...usersResponse.data,
        ]);
        setRelatedUsersDataResponse(usersResponse);
        setCriteria(updatedCriteria);
      },
    });
  }, [criteria, executeFetchMoreRelatedUsers, params.id, salesOrder.id]);

  const hasMoreRelatedUsers = useMemo(
    () => !!relatedUsersDataResponse.hasMore,
    [relatedUsersDataResponse]
  );

  useEffect(() => {
    if (!summaryLayout) {
      const fetchUsersCriteria = {
        ...initialUsersCriteria(),
        salesOrder: [salesOrder.number.toString()],
      };
      fetchRelatedUsers(fetchUsersCriteria);
    }
  }, [fetchRelatedUsers, salesOrder.number, searchParams, summaryLayout]);

  const tableConfig = useMemo(() => {
    const { search, sortBy, sortDirection } = criteria;
    return {
      search,
      sortBy,
      sortDirection,
    };
  }, [criteria]);

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters = criteria.search !== '';
    if (hasAppliedFilters) {
      return 'There are no users that match the selected criteria';
    }
    return 'There are no users at the moment';
  }, [criteria.search]);

  const workOrderNumbers = useMemo(
    () =>
      salesOrder.workOrders?.map((wo) => formatWorkOrderNumber(wo.number)) ??
      [],
    [salesOrder.workOrders]
  );

  const softwareTitlesLines = useMemo(() => {
    return salesOrder.lines.filter((sol) => sol.softwareSeat);
  }, [salesOrder.lines]);

  const devicesLines = useMemo(() => {
    return salesOrder.lines.filter((sol) => sol.device);
  }, [salesOrder.lines]);

  const devicesTableData = useMemo(() => {
    return Object.values(
      devicesLines.reduce(
        (acc: Record<string, SalesOrderDevicesTableRow>, deviceLine) => {
          const key = deviceLine.device!.model.displayName;
          if (!acc[key]) {
            acc[key] = {
              model: key,
              amount: 0,
            };
          }
          acc[key] = {
            ...acc[key],
            amount: acc[key].amount + 1,
          };

          return acc;
        },
        {}
      )
    ).slice(0, 10);
  }, [devicesLines]);

  const softwareTitlesTableData = useMemo(() => {
    // Group by software title and software type
    const groupedSoftwareTitlesMap = softwareTitlesLines.reduce(
      (acc: Record<string, SalesOrderSoftwareTitlesTableRow>, swLine) => {
        const licensingProductGroup =
          swLine.softwareSeat!.licensingProductGroup;
        const swTitle = licensingProductGroup.displayName;
        const swType = ApplicationVariantType.NATIVE;
        const key = `${swTitle}-${swType}`;

        if (!acc[key]) {
          acc[key] = {
            id: licensingProductGroup.id,
            key,
            title: swTitle,
            seats: 0,
            type: swType,
          };
        }
        acc[key] = {
          ...acc[key],
          seats: acc[key].seats + 1,
        };

        return acc;
      },
      {}
    );

    const groupedSoftwareTitles = Object.values(groupedSoftwareTitlesMap);

    const sortedSoftwareTitlesByName = groupedSoftwareTitles.sort((a, b) =>
      a.title.localeCompare(b.title)
    );

    return sortedSoftwareTitlesByName;
  }, [softwareTitlesLines]);

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<User, PermissionsGroupBySalesOrder>): void => {
      const newCriteria = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      fetchRelatedUsers(newCriteria);
    },
    [criteria, fetchRelatedUsers]
  );

  const navigateToEditUserPermissions = useCallback(
    (user: User) => {
      navigate(`/users/my-team/${user.id}/edit`);
    },
    [navigate]
  );

  const teamTable = useMemo(() => {
    return (
      <UsersSummaryTable
        onChange={handleOnTableChange}
        dataSource={adminViewMode ? relatedUsers : salesOrderRelatedUsers.users}
        config={tableConfig}
        fetchMore={fetchMoreRelatedUsers}
        hasMore={hasMoreRelatedUsers}
        empty={
          <Element display="flex" justifyContent="center" className="my-2">
            <span className="has-text-weight-light">{emptyContentText}</span>
          </Element>
        }
        handleUserTemporaryPermissionsClick={navigateToEditUserPermissions}
        adminViewMode={adminViewMode}
        relatedSalesOrderId={salesOrder.id}
      />
    );
  }, [
    handleOnTableChange,
    adminViewMode,
    relatedUsers,
    salesOrderRelatedUsers.users,
    tableConfig,
    fetchMoreRelatedUsers,
    hasMoreRelatedUsers,
    emptyContentText,
    navigateToEditUserPermissions,
    salesOrder.id,
  ]);

  return (
    <Columns className="p-4">
      <Columns
        display="flex"
        flexDirection="column"
        className={`pr-7 ${styles.informationColumn}`}
      >
        <Columns.Column>
          <DetailsCard title="Basic information">
            <InfoLabel
              icon={<FaBuilding />}
              label="Account"
              content={
                <Conditional condition={!!salesOrder.opportunity?.account}>
                  <Conditional.True>
                    <Conditional condition={!!summaryLayout}>
                      <Conditional.True>
                        <span>{salesOrder.opportunity?.account?.name}</span>
                      </Conditional.True>
                      <Conditional.False>
                        <Link
                          to={`/accounts/${salesOrder.opportunity?.account?.id}`}
                          className={styles.content}
                        >
                          {salesOrder.opportunity?.account?.name}
                        </Link>
                      </Conditional.False>
                    </Conditional>
                  </Conditional.True>
                  <Conditional.False>-</Conditional.False>
                </Conditional>
              }
            />
            <InfoLabel
              icon={<FaBuilding />}
              label="Parent account"
              content={
                <Conditional
                  condition={!!salesOrder.opportunity?.account?.parent}
                >
                  <Conditional.True>
                    <Conditional condition={!!summaryLayout}>
                      <Conditional.True>
                        <span>
                          {salesOrder.opportunity?.account?.parent?.name}
                        </span>
                      </Conditional.True>
                      <Conditional.False>
                        <Link
                          to={`/accounts/${salesOrder.opportunity?.account?.parent?.id}`}
                          className={styles.content}
                        >
                          {salesOrder.opportunity?.account?.parent?.name}
                        </Link>
                      </Conditional.False>
                    </Conditional>
                  </Conditional.True>
                  <Conditional.False>-</Conditional.False>
                </Conditional>
              }
            />
            <InfoLabel
              icon={<FaCalendar />}
              label="Close date"
              content={
                <span className={styles.content}>
                  {salesOrder.closeDate
                    ? formatDateToLocaleString(salesOrder.closeDate)
                    : '-'}
                </span>
              }
            />
            <If condition={!isCustomerUser && workOrderNumbers.length > 0}>
              <InfoLabel
                icon={<FaGear />}
                label="Work orders"
                content={workOrderNumbers.join(', ')}
              />
            </If>
          </DetailsCard>
        </Columns.Column>
        <Columns.Column>
          <DetailsCard title="Contacts">
            <InfoLabel
              icon={<FaUser />}
              label="End User Software Contact"
              content={
                <>
                  <If condition={!!salesOrder.endUser?.fullName}>
                    <span className={styles.content}>
                      {`${
                        salesOrder.endUser
                          ? `${salesOrder.endUser?.fullName}`
                          : ''
                      } `}
                    </span>
                  </If>
                  <span className={styles.content}>
                    {salesOrder.endUser?.email ?? '-'}
                  </span>
                </>
              }
            />
            <InfoLabel
              icon={<FaUser />}
              label="Billing Contact"
              content={
                <>
                  <If condition={!!salesOrder.billingContact?.fullName}>
                    <span className={styles.content}>
                      {`${
                        salesOrder.billingContact
                          ? `${salesOrder.billingContact.fullName}`
                          : ''
                      } `}
                    </span>
                  </If>
                  <span className={styles.content}>
                    {salesOrder.billingContact?.email ?? '-'}
                  </span>
                </>
              }
            />
            <InfoLabel
              icon={<FaUser />}
              label="Shipping Contact"
              content={
                <>
                  <If condition={!!salesOrder.shippingContact?.fullName}>
                    <span className={styles.content}>
                      {`${
                        salesOrder.shippingContact
                          ? `${salesOrder.shippingContact?.fullName}`
                          : ''
                      } `}
                    </span>
                  </If>
                  <span className={styles.content}>
                    {salesOrder.shippingContact?.email ?? '-'}
                  </span>
                </>
              }
            />
          </DetailsCard>
        </Columns.Column>
      </Columns>

      <Columns
        display="flex"
        flexDirection="column"
        className={`is-flex-grow-1 ${styles.informationColumn}`}
      >
        <Columns.Column display="flex" paddingless>
          <Columns.Column className="pb-0">
            <DetailsCard>
              <>
                <section className="pb-6 is-flex is-align-items-center">
                  <Icon color="primary" mr={2}>
                    <FaBolt size={20} />
                  </Icon>
                  <span className="is-size-4 has-text-weight-light">
                    {`${softwareTitlesTableData.length} Software ${
                      softwareTitlesTableData.length !== 1 ? 'Titles' : 'Title'
                    }`}
                  </span>
                </section>
                <SalesOrderSoftwareTitlesTable
                  data={softwareTitlesTableData}
                  salesOrder={salesOrder}
                />
              </>
            </DetailsCard>
          </Columns.Column>
          <Columns.Column>
            <DetailsCard>
              <>
                <section className="pb-6 is-flex is-align-items-center">
                  <Icon color="primary" mr={2}>
                    <FaLaptop size={24} />
                  </Icon>
                  <span className="is-size-4 has-text-weight-light">
                    {`${devicesLines.length} ${
                      devicesLines.length !== 1 ? 'Devices' : 'Device'
                    }`}
                  </span>
                </section>
                <SalesOrderDevicesTable data={devicesTableData} />
              </>
            </DetailsCard>
          </Columns.Column>
        </Columns.Column>
        <If condition={!summaryLayout}>
          <Columns.Column className={`pt-0 ${styles.tablesColumn}`}>
            <DetailsCard>
              <>
                <section className="pb-6 is-flex is-align-items-center">
                  <Icon color="primary" mr={2}>
                    <FaUser size={24} />
                  </Icon>
                  <span className="is-size-4 has-text-weight-light">Team</span>
                </section>
                <If condition={!!invalidUsersMessage}>
                  <FeedbackMessage>{invalidUsersMessage}</FeedbackMessage>
                </If>
                <Conditional condition={isLoadingMyDeviceGroups}>
                  <Conditional.True>
                    <PageSpinner />
                  </Conditional.True>
                  <Conditional.False>
                    <InboxTable
                      table={teamTable}
                      isTableEmpty={relatedUsers.length === 0}
                      config={tableConfig}
                      enableSearch={false}
                    />
                  </Conditional.False>
                </Conditional>
              </>
            </DetailsCard>
          </Columns.Column>
        </If>
      </Columns>
    </Columns>
  );
}

export default SalesOrderDetailsLayout;
